import type { Mode } from "@eldrun/content";
import type { PlanData } from "@eldrun/worldgen";
import type { ResponseOf, TerrainRequest, TerrainResponse } from "./protocol.ts";
import { chunkKey } from "./tile.ts";

/** Interface mínima de um Worker (permite testar com workers falsos). */
export interface WorkerLike {
  postMessage(message: TerrainRequest, transfer?: Transferable[]): void;
  onmessage: ((event: { data: TerrainResponse }) => void) | null;
  /** Falha fatal do worker (script não carregou, exceção não tratada, mensagem ilegível). */
  onerror: ((message: string) => void) | null;
  terminate(): void;
}

/** Adapta um `Worker` do navegador à interface mínima do pool. */
export function wrapWorker(worker: Worker): WorkerLike {
  let handler: WorkerLike["onmessage"] = null;
  let errorHandler: WorkerLike["onerror"] = null;
  worker.onmessage = (event: MessageEvent<TerrainResponse>) => handler?.({ data: event.data });
  worker.onerror = (event) => {
    event.preventDefault();
    errorHandler?.(event.message || "erro no worker de terreno");
  };
  worker.onmessageerror = () => errorHandler?.("mensagem ilegível do worker de terreno");
  return {
    postMessage: (message, transfer) => worker.postMessage(message, transfer ?? []),
    get onmessage() {
      return handler;
    },
    set onmessage(fn) {
      handler = fn;
    },
    get onerror() {
      return errorHandler;
    },
    set onerror(fn) {
      errorHandler = fn;
    },
    terminate: () => worker.terminate(),
  };
}

export class WorkerFailedError extends Error {}

type Pending = { resolve: (r: TerrainResponse) => void; reject: (e: Error) => void };

interface QueuedMesh {
  key: string;
  cx: number;
  cz: number;
  lod: number;
  priority: number;
  resolve: (r: ResponseOf<"mesh">) => void;
  reject: (e: Error) => void;
}

/** Máximo de malhas em andamento por worker: o resto espera na fila e pode ser repriorizado. */
const MESH_IN_FLIGHT = 2;

export class CancelledError extends Error {
  constructor() {
    super("pedido cancelado");
  }
}

/**
 * Pool de workers de terreno. Cada chunk pertence sempre ao mesmo worker (o que tem seu tile em
 * cache). Malhas entram numa fila por prioridade (menor = antes), sem duplicatas, canceláveis
 * enquanto não despachadas.
 */
export class TerrainWorkerPool {
  readonly size: number;
  private readonly workers: WorkerLike[];
  private readonly pending = new Map<number, Pending>();
  private readonly inFlight: number[];
  private readonly queue = new Map<string, QueuedMesh>();
  private nextId = 1;
  /** Motivo da falha fatal, se houve: o pool deixa de aceitar pedidos. */
  failure: Error | undefined;

  constructor(createWorker: () => WorkerLike, size: number) {
    this.size = Math.max(1, size);
    this.workers = Array.from({ length: this.size }, (_, index) => {
      const worker = createWorker();
      worker.onmessage = (event) => this.receive(event.data);
      worker.onerror = (message) => this.fail(new WorkerFailedError(`worker ${index}: ${message}`));
      return worker;
    });
    this.inFlight = new Array<number>(this.size).fill(0);
  }

  /** Planeja a ilha no worker 0 e envia o plano aos demais. */
  async plan(seed: number, mode: Mode): Promise<ResponseOf<"planned">> {
    const planned = (await this.request(0, { type: "plan", id: 0, seed, mode })) as ResponseOf<"planned">;
    await Promise.all(this.workers.slice(1).map((_, i) => this.request(i + 1, { type: "init", id: 0, plan: clonePlan(planned.plan) })));
    return planned;
  }

  /** Envia o heightfield montado a todos os workers (cada um recebe uma cópia). */
  async setHeightfield(heights: Float32Array): Promise<void> {
    await Promise.all(this.workers.map((_, i) => this.request(i, { type: "heightfield", id: 0, heights: heights.slice() })));
  }

  resources(cx: number, cz: number): Promise<ResponseOf<"resources">> {
    return this.request(this.owner(cx, cz), { type: "resources", id: 0, cx, cz }) as Promise<ResponseOf<"resources">>;
  }

  /** Enfeites do chão de um chunk (F44), no worker dono dele (depois do `setHeightfield`). */
  details(cx: number, cz: number): Promise<ResponseOf<"details">> {
    return this.request(this.owner(cx, cz), { type: "details", id: 0, cx, cz }) as Promise<ResponseOf<"details">>;
  }

  tile(cx: number, cz: number): Promise<ResponseOf<"tile">> {
    return this.request(this.owner(cx, cz), { type: "tile", id: 0, cx, cz }) as Promise<ResponseOf<"tile">>;
  }

  /** Enfileira uma malha; se já houver o mesmo pedido na fila, só atualiza a prioridade. */
  mesh(cx: number, cz: number, lod: number, priority: number): Promise<ResponseOf<"mesh">> {
    if (this.failure) return Promise.reject(this.failure);
    const key = `${cx},${cz},${lod}`;
    const existing = this.queue.get(key);
    if (existing) {
      existing.priority = Math.min(existing.priority, priority);
      return new Promise((resolve, reject) => {
        const previous = { resolve: existing.resolve, reject: existing.reject };
        existing.resolve = (r) => {
          previous.resolve(r);
          resolve(r);
        };
        existing.reject = (e) => {
          previous.reject(e);
          reject(e);
        };
      });
    }
    return new Promise((resolve, reject) => {
      this.queue.set(key, { key, cx, cz, lod, priority, resolve, reject });
      this.pump();
    });
  }

  /** Cancela uma malha ainda não despachada. Devolve `true` se havia algo a cancelar. */
  cancelMesh(cx: number, cz: number, lod: number): boolean {
    const key = `${cx},${cz},${lod}`;
    const queued = this.queue.get(key);
    if (!queued) return false;
    this.queue.delete(key);
    queued.reject(new CancelledError());
    return true;
  }

  updatePriority(cx: number, cz: number, lod: number, priority: number): void {
    const queued = this.queue.get(`${cx},${cz},${lod}`);
    if (queued) queued.priority = priority;
  }

  get stats() {
    return { queued: this.queue.size, inFlight: this.inFlight.reduce((a, b) => a + b, 0), pending: this.pending.size };
  }

  terminate(): void {
    for (const worker of this.workers) worker.terminate();
    this.rejectAll(new CancelledError());
  }

  /**
   * Falha fatal: um worker sem resposta deixaria pedidos presos para sempre. Encerra o pool e
   * rejeita tudo; quem usa o pool mostra o erro ao jogador.
   */
  private fail(error: Error): void {
    if (this.failure) return;
    this.failure = error;
    for (const worker of this.workers) worker.terminate();
    this.rejectAll(error);
  }

  private rejectAll(error: Error): void {
    const pending = [...this.pending.values()];
    const queued = [...this.queue.values()];
    this.pending.clear();
    this.queue.clear();
    this.inFlight.fill(0);
    for (const { reject } of pending) reject(error);
    for (const item of queued) item.reject(error);
  }

  private owner(cx: number, cz: number): number {
    return chunkKey(cx, cz) % this.size;
  }

  private request(workerIndex: number, message: TerrainRequest): Promise<TerrainResponse> {
    if (this.failure) return Promise.reject(this.failure);
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      (this.workers[workerIndex] as WorkerLike).postMessage({ ...message, id });
    });
  }

  private receive(response: TerrainResponse): void {
    const pending = this.pending.get(response.id);
    if (!pending) return;
    this.pending.delete(response.id);
    if (response.type === "error") pending.reject(new Error(response.message));
    else pending.resolve(response);
  }

  /** Despacha a fila em ordem de prioridade, respeitando o limite por worker. */
  private pump(): void {
    if (this.queue.size === 0) return;
    const sorted = [...this.queue.values()].sort((a, b) => a.priority - b.priority);
    for (const item of sorted) {
      const owner = this.owner(item.cx, item.cz);
      if ((this.inFlight[owner] as number) >= MESH_IN_FLIGHT) continue;
      this.queue.delete(item.key);
      this.inFlight[owner] = (this.inFlight[owner] as number) + 1;
      this.request(owner, { type: "mesh", id: 0, cx: item.cx, cz: item.cz, lod: item.lod })
        .then(
          (r) => item.resolve(r as ResponseOf<"mesh">),
          (e: Error) => item.reject(e),
        )
        .finally(() => {
          if (this.failure) return;
          this.inFlight[owner] = Math.max(0, (this.inFlight[owner] as number) - 1);
          this.pump();
        });
    }
  }
}

/** Cada worker recebe sua própria cópia das grades (o original pode ter sido transferido). */
function clonePlan(plan: PlanData): PlanData {
  return {
    ...plan,
    heights: plan.heights.slice(),
    moisture: plan.moisture.slice(),
    slope: plan.slope.slice(),
    coastDistance: plan.coastDistance.slice(),
    landDistance: plan.landDistance.slice(),
    biome: plan.biome.slice(),
    mainLand: plan.mainLand.slice(),
  };
}
